import { ProfileCardData } from '../components/Directory/ProfileCard'

// Author Mapper
export const mapAuthorToProfileCard = (author: any): ProfileCardData => {
  return {
    id: author.slug,
    // Using slug as ID for authors based on existing data structure
    name: author.name,
    image: author.avatar,
    role: 'Author',
    variant: 'author',
    rating: author.rating,
    reviewCount: author.reviewCount || 0,
    specialties: author.genres,
    location: author.location,
    profileUrl: `/author/${author.slug}`,
    bookCount: author.bookCount,
    bio: author.bio,
    featured: author.featured,
    // This might need to be passed from the featured section context if not on the object
    verified: true, // Assuming authors in directory are verified
  }
}

// Narrator Mapper
export const mapNarratorToProfileCard = (
  narrator: any,
  options?: { showPricing?: boolean },
): ProfileCardData => {
  return {
    id: narrator.id,
    name: narrator.name,
    image: narrator.avatar,
    role: 'Narrator',
    variant: 'narrator',
    rating: narrator.rating,
    reviewCount: 0,
    specialties: narrator.genres,
    location: narrator.location,
    profileUrl: `/narrators/${narrator.id}`,
    audiobookCount: narrator.booksNarrated,
    bio: narrator.bio,
    featured: narrator.featured,
    available: narrator.availability === 'Available',
    availabilityLabel: narrator.availability,
    verified: narrator.verified,
    // Only include pricing when explicitly requested (vendor-facing contexts)
    startingPrice: options?.showPricing ? narrator.startingPrice : undefined,
    portfolioThumbnails: narrator.recentBooks?.map((book: any) => book.cover),
  }
}

// Creative Mapper (AllCreativeProfiles)
export const mapCreativeToProfileCard = (creative: any): ProfileCardData => {
  return {
    id: creative.id,
    name: creative.name,
    image: creative.image,
    role: creative.title,
    variant: 'creative',
    rating: creative.rating,
    reviewCount: creative.reviewCount,
    specialties: creative.specialties,
    available: creative.available,
    availabilityLabel: creative.available ? 'Available' : 'Not Available',
    profileUrl: `/creative/${creative.category}/${creative.id}`,
    verified: true,
  }
}

// Service Mapper (ServiceDirectory)
export const mapServiceToProfileCard = (provider: any): ProfileCardData => {
  return {
    id: provider.id.toString(),
    name: provider.name,
    image: provider.image,
    role: provider.subcategory
      ? provider.subcategory.replace('-', ' ')
      : 'Service Provider',
    variant: 'service',
    rating: provider.rating,
    reviewCount: provider.reviewCount,
    specialties: provider.tags,
    location: provider.location,
    available: provider.availability === 'Available',
    availabilityLabel: provider.availability,
    startingPrice: provider.startingRate
      ? typeof provider.startingRate === 'number'
        ? `$${provider.startingRate} ${provider.rateUnit || ''}`
        : `${provider.startingRate}`
      : undefined,
    profileUrl: `/services/${provider.category}/${provider.id}`,
    bio: provider.bio,
    featured: provider.featured,
    verified: true,
  }
}

// Editor Mapper (EditingDirectory)
export const mapEditorToProfileCard = (editor: any): ProfileCardData => {
  // Extract rate from specialties if available
  const rate =
    editor.specialties && editor.specialties.length > 0
      ? editor.specialties[0].rate
      : undefined
  return {
    id: editor.id.toString(),
    name: editor.name,
    image: editor.image,
    role: editor.title,
    variant: 'service',
    rating: editor.rating,
    reviewCount: editor.reviews,
    specialties: editor.genres,
    // Editors often specialize by genre
    location: editor.location,
    available: editor.availability === 'Available',
    availabilityLabel: editor.availability,
    startingPrice: rate,
    profileUrl: `/services/editing/${editor.id}`,
    bio: editor.description,
    verified: editor.verified,
    // Add debut friendly tag to specialties if true
    ...(editor.debutFriendly
      ? {
          specialties: ['Debut Friendly', ...editor.genres],
        }
      : {}),
  }
}

// Production Mapper (ProductionDirectory)
export const mapProductionToProfileCard = (pro: any): ProfileCardData => {
  // Extract rate
  const rate =
    pro.specialties && pro.specialties.length > 0
      ? pro.specialties[0].rate
      : undefined
  return {
    id: pro.id.toString(),
    name: pro.name,
    image: pro.image,
    role: pro.title,
    variant: 'service',
    rating: pro.rating,
    reviewCount: pro.reviews,
    specialties: pro.formats,
    // Production pros specialize in formats
    available: pro.availability === 'Available',
    availabilityLabel: pro.availability,
    startingPrice: rate,
    profileUrl: `/services/production/${pro.id}`,
    bio: pro.description,
    verified: true,
    // Add debut friendly
    ...(pro.debutFriendly
      ? {
          specialties: ['Debut Friendly', ...pro.formats],
        }
      : {}),
  }
}

// Support Mapper (SupportDirectory)
export const mapSupportToProfileCard = (pro: any): ProfileCardData => {
  return {
    id: pro.id.toString(),
    name: pro.name,
    image: pro.image,
    role: pro.title,
    variant: 'service',
    rating: pro.rating,
    reviewCount: pro.reviews,
    specialties: pro.specialties,
    available: pro.availability === 'Available',
    availabilityLabel: pro.availability,
    profileUrl: `/services/support/${pro.id}`,
    verified: true,
  }
}
